import { pageTitle } from '@palaccount/config';
import type { ManufacturerResponse } from '@palaccount/contracts';
import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AuthorshipLine } from '@/features/master-data/authorship-line';
import { localizedName } from '@/lib/localized-name';
import { localizeServerMessage } from '@/lib/server-messages';

import { useManufacturerMutations, useManufacturers } from './api';
import { InventoryTabs } from './inventory-tabs';

interface FormState {
  name: string;
  /** S5: the optional Arabic twin of `name`. */
  nameAr: string;
}

const EMPTY: FormState = { name: '', nameAr: '' };

/**
 * Manufacturers (bugs: New Item — IT-S6; Stock levels — ST-02): the tiny
 * master behind the item form's Manufacturer pick and the two filters. The
 * tiers page's shape — list + one form card, switch on/off, no delete (a maker
 * that was ever named is history on the items that name it).
 */
export function ManufacturersPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<ManufacturerResponse | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [values, setValues] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = pageTitle(t('mfr.title'));
  }, [t]);

  const { data: manufacturers, isPending } = useManufacturers(showInactive);
  const { create, update, setActive } = useManufacturerMutations();

  const fail = (cause: unknown) =>
    setError(
      localizeServerMessage(t, cause instanceof Error ? cause.message : String(cause), cause),
    );

  const openNew = () => {
    setEditing(null);
    setValues(EMPTY);
    setError(null);
    setFormOpen(true);
  };
  const openEdit = (maker: ManufacturerResponse) => {
    setEditing(maker);
    setValues({ name: maker.name, nameAr: maker.nameAr ?? '' });
    setError(null);
    setFormOpen(true);
  };

  const submit = async () => {
    setError(null);
    if (values.name.trim() === '') return setError(t('mfr.errName'));
    const nameAr = values.nameAr.trim();
    try {
      // An emptied Arabic box CLEARS the twin on edit (null); on create it is
      // simply not sent — the tiers page's convention.
      if (editing) {
        await update.mutateAsync({
          manufacturerId: editing.id,
          body: { name: values.name.trim(), nameAr: nameAr === '' ? null : nameAr },
        });
      } else {
        await create.mutateAsync({
          name: values.name.trim(),
          ...(nameAr === '' ? {} : { nameAr }),
        });
      }
      setFormOpen(false);
    } catch (cause) {
      fail(cause);
    }
  };

  return (
    <div className="grid gap-4">
      <InventoryTabs />
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="me-auto text-lg font-bold">{t('mfr.title')}</h2>
        <Gated permission="inventory.create" reasonKey="perm.cannot.createManufacturers">
          <Button onClick={openNew}>
            <Plus aria-hidden /> {t('common.new')}
          </Button>
        </Gated>
      </div>
      <p className="text-sm text-muted-foreground">{t('mfr.hint')}</p>

      {formOpen ? (
        <Card>
          <CardHeader>
            <CardTitle>{t(editing ? 'mfr.editTitle' : 'mfr.newTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label htmlFor="m-name">{t('mfr.fName')}</Label>
                <Input
                  id="m-name"
                  maxLength={120}
                  value={values.name}
                  onChange={(event) => setValues({ ...values, name: event.target.value })}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="m-name-ar">{t('mfr.fNameAr')}</Label>
                <Input
                  id="m-name-ar"
                  dir="rtl"
                  lang="ar"
                  maxLength={120}
                  value={values.nameAr}
                  onChange={(event) => setValues({ ...values, nameAr: event.target.value })}
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Gated
                permission={editing ? 'inventory.edit' : 'inventory.create'}
                reasonKey={
                  editing ? 'perm.cannot.editManufacturers' : 'perm.cannot.createManufacturers'
                }
              >
                <Button onClick={() => void submit()} disabled={!values.name.trim()}>
                  {t('common.save')}
                </Button>
              </Gated>
              <Button variant="outline" onClick={() => setFormOpen(false)}>
                {t('common.cancel')}
              </Button>
              {editing ? (
                <Gated permission="inventory.edit" reasonKey="perm.cannot.editManufacturers">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setError(null);
                      setActive
                        .mutateAsync({ manufacturerId: editing.id, active: !editing.isActive })
                        .then(() => setFormOpen(false))
                        .catch(fail);
                    }}
                  >
                    {t(editing.isActive ? 'items.switchOff' : 'items.switchOn')}
                  </Button>
                </Gated>
              ) : null}
            </div>
            {editing ? <AuthorshipLine record={editing} /> : null}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardContent className="grid gap-4">
          <Label className="flex items-center gap-2 font-normal">
            <Checkbox
              checked={showInactive}
              onCheckedChange={(checked) => setShowInactive(checked === true)}
            />
            {t('common.showInactive')}
          </Label>
          {isPending ? (
            <div className="h-24 animate-pulse rounded-md bg-muted" aria-busy />
          ) : (manufacturers ?? []).length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">{t('mfr.empty')}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('mfr.fName')}</TableHead>
                  <TableHead>{t('mfr.fNameAr')}</TableHead>
                  <TableHead className="text-end">{t('mfr.colItems')}</TableHead>
                  <TableHead>{t('items.colStatus')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(manufacturers ?? []).map((maker) => (
                  <TableRow key={maker.id}>
                    <TableCell>
                      {/* The name opens the editor — the Items list's own convention. */}
                      <button
                        type="button"
                        className="text-start font-medium hover:underline"
                        aria-label={t('mfr.editNamed', { name: localizedName(maker, lang) })}
                        onClick={() => openEdit(maker)}
                      >
                        {localizedName(maker, lang)}
                      </button>
                    </TableCell>
                    <TableCell dir="rtl" lang="ar">
                      {maker.nameAr ?? ''}
                    </TableCell>
                    <TableCell className="text-end font-mono">{maker.itemCount}</TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground">
                        {t(maker.isActive ? 'common.active' : 'common.inactive')}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
